/** ログイン後の画面。タブ = ステージ / 単語 / まなび / 修正依頼。 */
import { useEffect, useMemo, useState } from 'react';
import type { AdminSession } from '../services/adminAuth';
import {
  LANGUAGES,
  LANGUAGE_LABELS,
  type Language,
  type LanguageContent,
} from '../services/content';
import type { ReadyBootstrap } from '../services/bootstrap';
import { ContentRequestService } from '../services/requests';
import { RequestDialog, type RequestTarget } from './RequestDialog';
import { RequestsPanel } from './RequestsPanel';
import { QuizBrowser } from './QuizBrowser';
import { StageBrowser } from './StageBrowser';
import { WordBrowser } from './WordBrowser';

type Tab = 'stages' | 'words' | 'quiz' | 'requests';

const TAB_LABELS: Readonly<Record<Tab, string>> = {
  stages: 'ステージ',
  words: '単語(横断)',
  quiz: 'まなび',
  requests: '修正依頼',
};

/** 言語セレクトが意味を持つタブ(まなび・修正依頼は言語で切り替えない)。 */
const LANGUAGE_TABS: readonly Tab[] = ['stages', 'words'];

interface Props {
  readonly boot: ReadyBootstrap;
  readonly session: AdminSession;
}

export function Console({ boot, session }: Props) {
  const [tab, setTab] = useState<Tab>('stages');
  const [language, setLanguage] = useState<Language>('japanese');
  const [content, setContent] = useState<LanguageContent | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [target, setTarget] = useState<RequestTarget | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  const requests = useMemo(
    () => new ContentRequestService(boot.firebase.db, session.uid),
    [boot.firebase.db, session.uid],
  );

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    setContentError(null);
    boot.content
      .load(language)
      .then((loaded) => {
        if (!cancelled) setContent(loaded);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setContentError(String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [boot.content, language]);

  return (
    <div className="shell">
      <header className="topbar">
        <h1 className="topbar__title">PaLTown 管理画面</h1>
        <div className="tabs">
          {(Object.keys(TAB_LABELS) as Tab[]).map((value) => (
            <button
              key={value}
              type="button"
              className={`tab${tab === value ? ' tab--active' : ''}`}
              onClick={() => {
                setTab(value);
              }}
            >
              {TAB_LABELS[value]}
            </button>
          ))}
        </div>
        {LANGUAGE_TABS.includes(tab) && (
          <select
            className="select"
            style={{ width: 'auto' }}
            value={language}
            onChange={(event) => {
              setLanguage(event.target.value as Language);
            }}
          >
            {LANGUAGES.map((value) => (
              <option key={value} value={value}>
                {LANGUAGE_LABELS[value]}
              </option>
            ))}
          </select>
        )}
        <div className="topbar__spacer" />
        <span className="muted mono">
          {session.email ?? session.uid}
          {boot.usingEmulator && ' (エミュレータ)'}
        </span>
        <button
          className="button button--small"
          type="button"
          onClick={() => {
            void boot.auth.signOut();
          }}
        >
          ログアウト
        </button>
      </header>

      {toast !== null && (
        <div className="notice notice--ok" role="status">
          {toast}
        </div>
      )}

      {contentError !== null && (
        <div className="notice notice--error" role="alert">
          コンテンツを読み込めませんでした: {contentError}
        </div>
      )}

      {tab === 'requests' ? (
        <RequestsPanel service={requests} reloadKey={reloadKey} />
      ) : tab === 'quiz' ? (
        <QuizBrowser loader={boot.quiz} assetsBase={boot.assetsBase} onRequest={setTarget} />
      ) : content === null ? (
        <div className="pane">
          <p className="muted">コンテンツを読み込み中…</p>
        </div>
      ) : tab === 'stages' ? (
        <StageBrowser content={content} assetsBase={boot.assetsBase} onRequest={setTarget} />
      ) : (
        <WordBrowser content={content} assetsBase={boot.assetsBase} onRequest={setTarget} />
      )}

      {target !== null && (
        <RequestDialog
          target={target}
          service={requests}
          onClose={() => {
            setTarget(null);
          }}
          onCreated={() => {
            setTarget(null);
            setReloadKey((value) => value + 1);
            setToast('修正メモを保存しました。「修正依頼」タブで確認できます。');
            window.setTimeout(() => {
              setToast(null);
            }, 4000);
          }}
        />
      )}
    </div>
  );
}
